// Consultas y mutaciones compartidas de casos: lista completa para el tablero, directorio de personas
// y edición con actualización optimista (se revierte si la API responde 4xx, en especial 409).
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { esApiError } from '../api/http';
import type { CambioCaso, Caso, CasoDetalle, CasoResumen, ListaCasos, UsuarioDirectorio } from '../api/tipos';
import { useUi } from '../shell/contexto';
import { USUARIOS_DEV } from '../auth/usuariosDev';
import { config } from '../config';

const PAGINA_TABLERO = 200;
const MAX_PAGINAS = 10;

/** Todos los casos (sigue el cursor) para el tablero, los contadores y los filtros. */
export function useTodosLosCasos() {
  return useQuery({
    queryKey: ['casos', 'todos'],
    queryFn: async (): Promise<ListaCasos> => {
      const items: CasoResumen[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < MAX_PAGINAS; i++) {
        const r = await api.listarCasos({ limite: PAGINA_TABLERO, cursor });
        items.push(...r.items);
        if (!r.siguiente_cursor) break;
        cursor = r.siguiente_cursor;
      }
      return { items, siguiente_cursor: null };
    },
  });
}

/** Directorio para asignar y mencionar. Si la API aún no expone /usuarios, se usa el directorio de prueba en modo dev. */
export function useUsuarios() {
  return useQuery({
    queryKey: ['usuarios'],
    queryFn: async (): Promise<UsuarioDirectorio[]> => {
      try {
        return await api.usuarios();
      } catch (e) {
        if (config.modoAuth === 'dev' && esApiError(e) && e.status === 404) return USUARIOS_DEV.map((u) => ({ ...u }));
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/** Nombre visible por oid, combinando el directorio y los nombres que trae la lista de casos. */
export function useNombres(casos?: CasoResumen[]) {
  const usuarios = useUsuarios();
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const c of casos ?? []) if (c.dueno && c.dueno_nombre) m.set(c.dueno, c.dueno_nombre);
    for (const u of usuarios.data ?? []) m.set(u.oid, u.nombre);
    m.set('sistema', 'Sistema');
    return m;
  }, [casos, usuarios.data]);
}

export interface CambioPedido {
  caso: { case_id: string; version?: number };
  cambio: Omit<CambioCaso, 'version'>;
  /** Campos visibles extra para la actualización optimista (p. ej. `dueno_nombre`). */
  visible?: Partial<CasoResumen>;
  /** Texto del aviso de éxito; sin texto no se avisa. */
  exito?: string;
  silencioso?: boolean;
}

export function mensajeError(e: unknown, caseId?: string): { texto: string; detalle?: string; regla?: string | null } {
  if (esApiError(e)) {
    if (e.status === 409 && e.codigo.includes('version'))
      return { texto: `Otra persona modificó ${caseId ?? 'el caso'}`, detalle: 'Se recargaron los datos; revise y vuelva a intentarlo.' };
    if (e.status === 403) return { texto: 'Sin permiso', detalle: e.detalle, regla: e.regla };
    if (e.status === 422) return { texto: 'Bloqueado por una regla de negocio', detalle: e.detalle, regla: e.regla };
    if (e.status === 409) return { texto: 'Cambio no permitido', detalle: e.detalle, regla: e.regla };
    return { texto: 'No se pudo guardar', detalle: e.detalle, regla: e.regla };
  }
  return { texto: 'No se pudo guardar', detalle: 'Ocurrió un error inesperado.' };
}

export function useCambiarCaso() {
  const qc = useQueryClient();
  const ui = useUi();
  return useMutation({
    mutationFn: async ({ caso, cambio }: CambioPedido): Promise<Caso> => {
      let version = caso.version;
      if (version === undefined) version = (await qc.fetchQuery({ queryKey: ['caso', caso.case_id], queryFn: () => api.obtenerCaso(caso.case_id) })).caso.version;
      return api.actualizarCaso(caso.case_id, { version, ...cambio });
    },
    onMutate: async ({ caso, cambio, visible }) => {
      await qc.cancelQueries({ queryKey: ['casos'] });
      const previos = qc.getQueriesData<ListaCasos>({ queryKey: ['casos'] });
      const previoDetalle = qc.getQueryData<CasoDetalle>(['caso', caso.case_id]);
      const parche = { ...(cambio as Partial<CasoResumen>), ...visible };
      delete (parche as Record<string, unknown>).tema;
      if (cambio.tema) parche.tema = cambio.tema;
      qc.setQueriesData<ListaCasos>({ queryKey: ['casos'] }, (d) =>
        d && Array.isArray(d.items) ? { ...d, items: d.items.map((x) => (x.case_id === caso.case_id ? { ...x, ...parche } : x)) } : d,
      );
      if (previoDetalle) qc.setQueryData<CasoDetalle>(['caso', caso.case_id], { ...previoDetalle, caso: { ...previoDetalle.caso, ...(parche as Partial<Caso>) } });
      return { previos, previoDetalle };
    },
    onError: (e, { caso }, ctx) => {
      for (const [k, v] of ctx?.previos ?? []) qc.setQueryData(k, v);
      if (ctx?.previoDetalle) qc.setQueryData(['caso', caso.case_id], ctx.previoDetalle);
      const m = mensajeError(e, caso.case_id);
      ui.avisar({ tono: 'error', texto: m.texto, detalle: m.detalle, regla: m.regla });
      void qc.invalidateQueries({ queryKey: ['casos'] });
      void qc.invalidateQueries({ queryKey: ['caso', caso.case_id] });
    },
    onSuccess: (nuevo, { caso, exito }) => {
      qc.setQueriesData<ListaCasos>({ queryKey: ['casos'] }, (d) =>
        d && Array.isArray(d.items)
          ? { ...d, items: d.items.map((x) => (x.case_id === caso.case_id ? { ...x, version: nuevo.version, actualizado: nuevo.actualizado, estado: nuevo.estado } : x)) }
          : d,
      );
      if (exito) ui.avisar({ tono: 'exito', texto: exito });
    },
    onSettled: (_r, _e, { caso }) => {
      void qc.invalidateQueries({ queryKey: ['caso', caso.case_id] });
      void qc.invalidateQueries({ queryKey: ['actividad', caso.case_id] });
      void qc.invalidateQueries({ queryKey: ['mi-trabajo'] });
      void qc.invalidateQueries({ queryKey: ['casos'] });
    },
  });
}

/** Todas las etiquetas en uso (para sugerir). */
export function useEtiquetasConocidas(): string[] {
  const todos = useTodosLosCasos();
  return useMemo(() => [...new Set((todos.data?.items ?? []).flatMap((c) => c.etiquetas ?? []))].sort(), [todos.data]);
}

